import { Component, ReactNode } from "react";
import i18next from "src/i18n/config";

import { showErrorToast } from "@shared/toasts/Toasts";

type Props = {
    children: ReactNode;
    onError?: () => void;
};

type State = {
    error: unknown;
};

export class ChunkLoadError extends Error {
    constructor(cause: unknown) {
        super("Failed to load a code-split chunk", { cause });
        this.name = "ChunkLoadError";
    }
}

export class ChunkLoadErrorBoundary extends Component<Props, State> {
    state: State = { error: null };

    static getDerivedStateFromError(error: unknown) {
        return { error };
    }

    private isChunkLoadError(error: unknown) {
        return error instanceof ChunkLoadError;
    }

    componentDidCatch(error: unknown) {
        const isChunkLoadError = this.isChunkLoadError(error);
        if (isChunkLoadError && this.props?.onError) {
            this.props.onError();
        }
        if (isChunkLoadError) {
            showErrorToast(i18next.t("common:error.failedToLoadFeature"));
        }
    }

    render() {
        if (this.isChunkLoadError(this.state.error)) {
            return null;
        }
        if (this.state.error) {
            throw this.state.error;
        }
        return this.props.children;
    }
}
